"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useValidationChangeTaskUpdate = void 0;
const react_1 = __importDefault(require("react"));
const TaskCompletenessContext_1 = require("../../context/TaskCompletenessContext");
const constants_1 = require("../constants");
const ValidationContext_1 = require("../../context/ValidationContext");
const __1 = require("..");
const __2 = require("../..");
const react_redux_1 = require("react-redux");
const validation_1 = require("../../view/pages/tasks/pricing/validation");
const useExperiment_1 = require("../../hooks/useExperiment");
/**
 * This hook will react to any changes in validation state (either locally in ValidationContext, or via network responses
 * for pricing/availability).
 *
 * Upon receiving changed validation state, it will determine if that now means that Task "completeness" state should change,
 * and dispatches any required updates.
 *
 * @returns null: This is a logic-only component that needs to be mounted to the component tree.
 */
const useValidationChangeTaskUpdate = () => {
    const { state: validationState } = react_1.default.useContext(ValidationContext_1.ValidationContext);
    const { dispatch: completenessDispatch } = react_1.default.useContext(TaskCompletenessContext_1.TaskCompletenessContext);
    const { propertyId, customerId } = react_1.default.useContext(__2.AppContext);
    const pricingValidation = (0, react_redux_1.useSelector)((state) => state.data.pricingValidation.byPropertyId[propertyId]);
    const pricingDefaults = (0, react_redux_1.useSelector)((state) => state.data.pricingDefaults.byPropertyId[propertyId]);
    const pricingType = (0, react_redux_1.useSelector)((state) => state.data.pricingType.byPropertyId[propertyId]);
    const customerExperiments = (0, useExperiment_1.useCustomerExperiments)(customerId);
    const isBasePriceExperiment = (customerExperiments === null || customerExperiments === void 0 ? void 0 : customerExperiments.smart_pricing_base_rate_ui) === 'on';
    // Because pricing validation is (usually) handled via a back-end service call, we control it here instead of ValidationContext.
    // For manual pricing type, validation is stored in UI-entities and refreshed on update to manual pricing (see <PricingPage>).
    // For automatic pricing type, we validate the pricing defaults data locally and use its validity for the pricing task.
    react_1.default.useEffect(() => {
        if ((pricingType === null || pricingType === void 0 ? void 0 : pricingType.type) === 'automatic') {
            if (pricingDefaults) {
                const allValidDefaults = Object.values((0, validation_1.validatePricingDefaults)(pricingDefaults, isBasePriceExperiment)).every((v) => v);
                completenessDispatch({ type: 'UPDATE_TASK_VALIDATION', payload: { [constants_1.Tasks.PRICING]: allValidDefaults } });
            }
        }
        else {
            if (pricingValidation) {
                completenessDispatch({ type: 'UPDATE_TASK_VALIDATION', payload: { [constants_1.Tasks.PRICING]: pricingValidation.valid } });
            }
        }
    }, [pricingValidation, pricingType, pricingDefaults]);
    const availabilityValidation = (0, react_redux_1.useSelector)((state) => state.data.propertySummaries.byId[propertyId]);
    // Availability validation is via property summary, so not tracked in ValidationContext
    react_1.default.useEffect(() => {
        if (availabilityValidation) {
            completenessDispatch({ type: 'UPDATE_TASK_VALIDATION', payload: { [constants_1.Tasks.BOOKING_AVAILABILITY]: availabilityValidation.validation.availability.valid } });
        }
    }, [availabilityValidation]);
    react_1.default.useEffect(() => {
        const stateUpdates = {};
        Object.keys(__1.TaskDetails).forEach((k) => {
            const requirements = __1.TaskDetails[k].validationRequirements;
            if (!requirements)
                return;
            switch (requirements.type) {
                case 'property':
                    if (validationState.property.initialised) {
                        stateUpdates[k] = requirements.propertyFields.every((val) => validationState.property.fields[val]);
                    }
                    break;
                case 'customer':
                    if (validationState.customer.initialised) {
                        stateUpdates[k] = requirements.customerFields.every((val) => validationState.customer.fields[val]);
                    }
                    break;
                case 'image':
                    if (validationState.images.initialised) {
                        stateUpdates[k] = validationState.images.valid;
                    }
                    break;
                case 'channels':
                    if (validationState.channels.initialised) {
                        stateUpdates[k] = validationState.channels.valid;
                    }
                    break;
                case 'local_licence':
                    if (validationState.local_licence.initialised) {
                        stateUpdates[k] = validationState.local_licence.valid;
                    }
                    break;
                case 'terms':
                    if (validationState.terms.initialised) {
                        stateUpdates[k] = validationState.terms.valid;
                    }
                    break;
                default:
                    break;
            }
        });
        if (Object.keys(stateUpdates).length) {
            completenessDispatch({ type: 'UPDATE_TASK_VALIDATION', payload: stateUpdates });
        }
    }, [validationState]);
    return null;
};
exports.useValidationChangeTaskUpdate = useValidationChangeTaskUpdate;
//# sourceMappingURL=validationListener.js.map